import React, { useEffect, useState, useRef } from 'react'
import { List } from 'devextreme-react/list'
import axios from 'axios'
import notify from '../../utils/notify'
import { UploadDialog, TimestampDisplay, MultipleFilesImportPopup, ExtractionReviewDialog } from '../../components'
import type { ExtractionReview, ExtractionReviewData, ExtractionReviewUi } from '../../components'
import { Button as FormButton } from 'devextreme-react/button'
import { useLocation } from 'react-router-dom'
import { socketGet } from '../../socket'
import { saveAs } from 'file-saver'
import DataGrid, { Column, HeaderFilter, Selection, Paging, Button, FilterRow } from 'devextreme-react/data-grid'
import { LoadIndicator } from 'devextreme-react/load-indicator'
import { useAuth } from '../../contexts/auth'
import useMultipleFilesGrouping from '../../hooks/useMultipleFilesGrouping'

interface Warning {
	text: string
}

function fileHashFor(items: { file?: string; rows?: string; hash?: string }[], filename: string): string | null {
	const pending = items.find(
		item => item.file === filename && String(item.rows).toLowerCase() === 'pending' && !!item.hash
	)
	if (pending?.hash) {
		return pending.hash
	}
	const match = items.find(item => item.file === filename && !!item.hash)
	return match?.hash ?? null
}

function asReview(payload: {
	filename?: string
	extraction_run_id?: number | null
	file_hash?: string | null
	data?: ExtractionReviewData
	ui?: ExtractionReviewUi
}): ExtractionReview | null {
	if (!payload.filename || !payload.data || !payload.ui?.fields?.length) {
		return null
	}
	return {
		filename: payload.filename,
		extraction_run_id: payload.extraction_run_id ?? null,
		file_hash: typeof payload.file_hash === 'string' && payload.file_hash !== '' ? payload.file_hash : null,
		data: payload.data,
		ui: payload.ui,
	}
}

export default function Imports() {
	const [items, setItems] = useState<any>([])
	const [users, setUsers] = useState<any>({})
	const [warnings, setWarnings] = useState<Warning[]>([])
	const [text, setText] = useState('')
	const [showDialog, setShowDialog] = useState(false)
	const [refresh, setRefresh] = useState(0)
	const [rowsTitle, setRowsTitle] = useState('Rows Imported')
	const [hideImportedFiles, setHideImportedFiles] = useState(true)
	const [successMessage, setSuccessMessage] = useState<boolean>(false)
	const [multipleFilesPopup, setMultipleFilesPopup] = useState(false)
	const [outcomes, setOutcomes] = useState<{ show: boolean; counts: Record<string, any> }>({
		show: false,
		counts: {},
	})
	const [reviewBeforeConfirm, setReviewBeforeConfirm] = useState(false)
	const [reviews, setReviews] = useState<ExtractionReview[]>([])
	const [confirmingReview, setConfirmingReview] = useState(false)

	const gridRef = useRef<DataGrid | null>(null)

	const location = useLocation()
	const searchParams = new URLSearchParams(location.search)
	const process = searchParams.get('process')
	const socket = socketGet()

	const { user } = useAuth()

	const { primaryItems, getSiblings, handleShowSiblings, resultFiles, closeResultFiles } = useMultipleFilesGrouping(
		items,
		multipleFilesPopup
	)

	const outcomeValue = (data: any, key: string) => outcomes.counts[data.unique_id]?.[key] ?? null

	useEffect(() => {
		const handlerWarning = (data: any) => {
			const { warning } = data
			if (warning.process === process) {
				const { message, line } = warning
				const text = line > 0 ? `${message} on line ${line}` : message
				setWarnings(warnings => [...warnings, { text }])
			}
		}

		const handlerReview = (payload: any) => {
			if (payload.process && payload.process !== process) {
				return
			}
			const review = asReview(payload)
			if (!review) {
				return
			}
			setReviews(current => {
				const without = current.filter(item => item.filename !== review.filename)
				return [...without, review]
			})
		}

		if (socket) {
			socket.on('legacy_import_warning', handlerWarning)
			socket.on('legacy_import_extraction_review', handlerReview)

			return () => {
				socket.off('legacy_import_warning', handlerWarning)
				socket.off('legacy_import_extraction_review', handlerReview)
			}
		}
	}, [socket, process])

	useEffect(() => {
		axios
			.get(`${API_FIBER_URL}/imports?process=${process}`)
			.then(response => response.data)
			.then(data => {
				setItems(data.list)
				setUsers(data.users)
				setText(data.text)
				setHideImportedFiles(data.hideImportedFiles)
				setMultipleFilesPopup(data.multipleFilesPopup ?? false)
				setReviewBeforeConfirm(data.reviewBeforeConfirm ?? false)
				if (data.rowsTitle !== null) {
					setRowsTitle(data.rowsTitle)
				}
				document.title = `Fiber :: ${data.text}`
			})
		axios
			.get(`${API_FIBER_URL}/imports/outcomes?process=${process}`)
			.then(response => response.data)
			.then(data => setOutcomes({ show: data.show ?? false, counts: data.counts ?? {} }))
	}, [process, refresh])

	const downloadHash = (hash: string, filename: string) => {
		axios({
			method: 'get',
			url: `${API_FIBER_URL}/imports/file/${encodeURIComponent(hash)}/download?process=${process}`,
			responseType: 'blob',
		}).then(response => {
			const { data, headers } = response
			const blob = new Blob([data], { type: headers['content-type'] })
			saveAs(blob, filename)
		})
	}

	const openPendingReview = (filename?: string) => {
		const params = new URLSearchParams({ process: process as string })
		if (filename) {
			params.set('filename', filename)
		}
		return axios
			.get(`${API_FIBER_URL}/imports/pending-review?${params.toString()}`)
			.then(response => {
				if (!response.data.success) {
					notify(response.data.message || 'No extracted values to review', 'error')
					return
				}

				const incoming: ExtractionReview[] = []
				if (Array.isArray(response.data.reviews)) {
					for (const item of response.data.reviews) {
						const review = asReview(item)
						if (review) {
							incoming.push(review)
						}
					}
				} else {
					const review = asReview(response.data)
					if (review) {
						incoming.push(review)
					}
				}

				if (incoming.length === 0) {
					notify(
						response.data.data
							? 'Review UI schema missing from server response'
							: response.data.message || 'No extracted values to review',
						'error'
					)
					return
				}

				setReviews(current => {
					const byName = new Map(current.map(item => [item.filename, item]))
					for (const review of incoming) {
						byName.set(review.filename, review)
					}
					return Array.from(byName.values())
				})
			})
			.catch(error => {
				notify(error.response?.data?.message || 'Could not load extracted values', 'error')
			})
	}

	const confirmReview = (data: ExtractionReviewData, review: ExtractionReview) => {
		setConfirmingReview(true)
		axios
			.post(`${API_FIBER_URL}/imports/confirm-extraction`, {
				process,
				extraction_run_id: review.extraction_run_id,
				filename: review.filename,
				data,
			})
			.then(response => {
				if (!response.data.success) {
					notify(response.data.message || 'Confirm failed', 'error')
					return
				}
				const order = String(data.order_number ?? '')
				const lines = response.data.lines_upserted ?? 0
				notify(
					order
						? `PO ${order} saved to Sales Orders (${lines} line${lines === 1 ? '' : 's'})`
						: 'Purchase Order confirmed',
					'success'
				)
				setReviews(currentReviews => currentReviews.filter(item => item.filename !== review.filename))
				setSuccessMessage(true)
				setRefresh(refresh + 1)
			})
			.catch(error => {
				notify(error.response?.data?.message || 'Confirm failed', 'error')
			})
			.finally(() => setConfirmingReview(false))
	}

	const discardReview = (review: ExtractionReview) => {
		setReviews(current => current.filter(item => item.filename !== review.filename))
	}

	const viewLogWarnings = async (e: any) => {
		const hash = e.row.data.log_hash
		// Select the current row
		if (gridRef.current) {
			gridRef.current.instance.selectRows([e.row.key], false)
		}

		axios
			.get(`${API_FIBER_URL}/imports/logfile/${encodeURIComponent(hash)}`)
			.then(response => response.data)
			.then(data => {
				setWarnings(data.warnings.map((warning: string) => ({ text: warning })))
			})
	}

	if (!text) {
		return (
			<h2 className={'content-block'}>
				<LoadIndicator height={20} width={20} />
			</h2>
		)
	}

	return (
		<React.Fragment>
			<h2 className={'content-block'}>
				<span className={'dx-icon-upload'} style={{ fontSize: '24px' }}></span> {text}
			</h2>
			<div className={'content-block'}>
				<FormButton
					icon="upload"
					text="Import new file"
					width={'400px'}
					height={'100px'}
					onClick={() => {
						setSuccessMessage(false)
						setWarnings([])
						setShowDialog(true)
					}}
				/>
			</div>
			<div className={'content-block'}>
				{successMessage ? (
					<p style={{ fontSize: '18px', color: 'green' }}>Import process has been completed</p>
				) : null}
			</div>
			<br />
			{warnings.length > 0 && (
				<div className={'content-block'}>
					<h2>Warnings:</h2>
					<List className={'warningList'} items={warnings} displayExpr="text" />
				</div>
			)}
			{!hideImportedFiles && (
				<div className={'content-block'}>
					<h2>Imported files:</h2>
					<DataGrid
						ref={gridRef}
						dataSource={primaryItems}
						rowAlternationEnabled={true}
						showBorders={true}
						width="100%"
						onSelectionChanged={() => setWarnings([])}
					>
						<Paging defaultPageSize={15} defaultPageIndex={0} />
						<Selection mode="single" />
						<Column dataField="file" />
						<Column type="buttons" width="40">
							<Button
								icon="warning"
								hint="Show warnings"
								onClick={e => viewLogWarnings(e)}
								visible={e => e.row.data.log_hash !== null}
							/>
						</Column>
						<Column
							dataField="rows"
							caption={rowsTitle}
							width="140"
							alignment="center"
							calculateCellValue={(data: any) => outcomeValue(data, 'rows') ?? data.rows}
						/>
						<Column
							caption="Inserted"
							width="90"
							alignment="center"
							visible={outcomes.show}
							calculateCellValue={(data: any) => outcomeValue(data, 'inserted')}
						/>
						<Column
							caption="Updated"
							width="90"
							alignment="center"
							visible={outcomes.show}
							calculateCellValue={(data: any) => outcomeValue(data, 'updated')}
						/>
						<Column
							caption="Unchanged"
							width="100"
							alignment="center"
							visible={outcomes.show}
							calculateCellValue={(data: any) => outcomeValue(data, 'unchanged')}
						/>
						<Column
							caption="Skipped"
							width="90"
							alignment="center"
							visible={outcomes.show}
							calculateCellValue={(data: any) => outcomeValue(data, 'skipped')}
						/>
						<Column
							dataField="date"
							width="120"
							alignment="center"
							cellRender={({ data }) => {
								return <TimestampDisplay timestamp={data['time']}></TimestampDisplay>
							}}
						/>
						<Column
							dataField="user"
							width="150"
							alignment="center"
							cellRender={({ data }) => {
								const email = (users[data['user']] ?? '').split('@')[0]
								return user?.email === email ? <strong>{email}</strong> : email
							}}
						/>
						<Column type="buttons" width="140">
							<Button
								icon="folder"
								hint="Show related files"
								onClick={handleShowSiblings}
								visible={e => getSiblings(e.row.data).length > 0}
							/>
							<Button
								icon="edit"
								hint="Review extracted values"
								onClick={e => openPendingReview(e.row!.data.file)}
								visible={e => String(e.row?.data.rows).toLowerCase() === 'pending'}
							/>
							<Button
								icon="download"
								hint="Download"
								onClick={e => downloadHash(e.row!.data.hash, e.row!.data.file)}
							/>
						</Column>
					</DataGrid>
					<HeaderFilter visible={true} />
				</div>
			)}
			{showDialog && (
				<UploadDialog
					onHide={() => setShowDialog(false)}
					onComplete={() => {
						setShowDialog(false)
						if (reviewBeforeConfirm) {
							notify('Extraction complete — review the values before confirming', 'success')
							setRefresh(refresh + 1)
							openPendingReview()
							return
						}
						notify('File uploaded successfully', 'success')
						setTimeout(() => {
							setSuccessMessage(true)
							setRefresh(refresh + 1)
						}, 5000)
					}}
					onAbort={() => setRefresh(refresh + 1)}
					onErrorFields={() => {
						notify('Error uploading file, columns missing', 'error')
					}}
				/>
			)}
			{resultFiles && (
				<MultipleFilesImportPopup
					resultFiles={resultFiles}
					rowsTitle={rowsTitle}
					onDownload={downloadHash}
					onHide={closeResultFiles}
				/>
			)}
			{reviews.length > 0 && (
				<ExtractionReviewDialog
					reviews={reviews.map(review =>
						review.file_hash ? review : { ...review, file_hash: fileHashFor(items, review.filename) }
					)}
					process={process}
					confirming={confirmingReview}
					onConfirm={confirmReview}
					onDiscard={discardReview}
					onHide={() => setReviews([])}
				/>
			)}
		</React.Fragment>
	)
}
